import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Organization, ProjectInput } from '../types';
import type { OrgDocumentKey } from '../prompts/orgDocuments';
import { useAuth } from './useAuth';
import { OrganizationsContext, type OrganizationsContextValue, type OrgDocumentGenerationResult } from './OrganizationsContext';

const ACTIVE_ROOT = 'scalent-active-organization';

function activeKey(namespace: string) {
  return `${ACTIVE_ROOT}-${namespace}`;
}

async function fetchOrganizationsFromApi(): Promise<Organization[]> {
  try {
    const res = await fetch('/api/organizations');
    if (!res.ok) return [];
    const data = (await res.json()) as { organizations: Organization[] };
    return data.organizations || [];
  } catch {
    return [];
  }
}

async function postOrganizationToApi(payload: Partial<Organization> & { name: string; idea: string; projectId?: string }): Promise<Organization | null> {
  try {
    const res = await fetch('/api/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { organization: Organization };
    return data.organization;
  } catch {
    return null;
  }
}

export function OrganizationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const namespace = user?.id || 'anon';

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(null);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);

  // Initial load + active id restore
  useEffect(() => {
    let cancelled = false;
    setLoadingOrganizations(true);
    (async () => {
      const list = await fetchOrganizationsFromApi();
      if (cancelled) return;
      setOrganizations(list);
      try {
        const raw = localStorage.getItem(activeKey(namespace));
        if (raw && list.some((o) => o.id === raw)) {
          setActiveOrganizationIdState(raw);
        } else {
          setActiveOrganizationIdState(null);
        }
      } catch {
        setActiveOrganizationIdState(null);
      }
      setLoadingOrganizations(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [namespace]);

  // Persist active organization id
  useEffect(() => {
    try {
      if (activeOrganizationId) localStorage.setItem(activeKey(namespace), activeOrganizationId);
      else localStorage.removeItem(activeKey(namespace));
    } catch {
      /* localStorage unavailable */
    }
  }, [activeOrganizationId, namespace]);

  const setActiveOrganizationId = (id: string | null) => {
    setActiveOrganizationIdState(id);
  };

  const refreshOrganizations = useCallback(async () => {
    const list = await fetchOrganizationsFromApi();
    setOrganizations(list);
  }, []);

  const createOrganization = useCallback(
    async (name: string, input: ProjectInput, projectId?: string): Promise<Organization> => {
      const created = await postOrganizationToApi({
        name,
        idea: input.idea || name,
        industry: input.industry,
        location: input.location,
        teamSize: input.teamSize,
        stage: input.stage,
        goals: input.goals,
        locale: input.locale,
        workingDoc: '',
        extract: {},
        projectId,
      });
      if (created) {
        setOrganizations((prev) => [created, ...prev]);
        setActiveOrganizationIdState(created.id);
        return created;
      }
      // Fallback: a local shape so the UI doesn't break on a network miss.
      const fallback: Organization = {
        id: crypto.randomUUID(),
        userId: namespace,
        name,
        idea: input.idea || name,
        industry: input.industry || null,
        location: input.location || null,
        teamSize: input.teamSize || null,
        stage: input.stage || null,
        goals: input.goals || null,
        locale: (input.locale === 'de' ? 'de' : 'en'),
        workingDoc: '',
        extract: {},
        interviewSessionId: null,
        linkedProjectIds: projectId ? [projectId] : [],
        documents: {},
        documentsStatus: {},
        documentsErrors: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setOrganizations((prev) => [fallback, ...prev]);
      setActiveOrganizationIdState(fallback.id);
      return fallback;
    },
    [namespace]
  );

  const updateOrganization = useCallback(
    async (org: Organization): Promise<void> => {
      setOrganizations((prev) =>
        prev.map((o) => (o.id === org.id ? { ...org, updatedAt: new Date().toISOString() } : o))
      );
      try {
        const res = await fetch(`/api/organizations/${org.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: org.name,
            idea: org.idea,
            industry: org.industry,
            location: org.location,
            teamSize: org.teamSize,
            stage: org.stage,
            goals: org.goals,
            locale: org.locale,
            workingDoc: org.workingDoc,
            extract: org.extract,
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as { organization: Organization };
          setOrganizations((prev) => prev.map((o) => (o.id === org.id ? data.organization : o)));
        }
      } catch {
        /* keep optimistic state */
      }
    },
    []
  );

  const deleteOrganization = useCallback(
    async (id: string): Promise<void> => {
      setOrganizations((prev) => prev.filter((o) => o.id !== id));
      if (activeOrganizationId === id) setActiveOrganizationIdState(null);
      try {
        await fetch(`/api/organizations/${id}`, { method: 'DELETE' });
      } catch {
        /* keep optimistic state */
      }
    },
    [activeOrganizationId]
  );

  const linkToProject = useCallback(async (orgId: string, projectId: string): Promise<void> => {
    setOrganizations((prev) =>
      prev.map((o) =>
        o.id === orgId
          ? {
              ...o,
              linkedProjectIds: o.linkedProjectIds.includes(projectId)
                ? o.linkedProjectIds
                : [...o.linkedProjectIds, projectId],
            }
          : o
      )
    );
    try {
      const res = await fetch(`/api/organizations/${orgId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      if (res.ok) {
        const data = (await res.json()) as { organization: Organization };
        setOrganizations((prev) => prev.map((o) => (o.id === orgId ? data.organization : o)));
      }
    } catch {
      /* keep optimistic state */
    }
  }, []);

  const unlinkFromProject = useCallback(async (orgId: string, projectId: string): Promise<void> => {
    setOrganizations((prev) =>
      prev.map((o) =>
        o.id === orgId
          ? { ...o, linkedProjectIds: o.linkedProjectIds.filter((p) => p !== projectId) }
          : o
      )
    );
    try {
      const res = await fetch(`/api/organizations/${orgId}/unlink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      if (res.ok) {
        const data = (await res.json()) as { organization: Organization };
        setOrganizations((prev) => prev.map((o) => (o.id === orgId ? data.organization : o)));
      }
    } catch {
      /* keep optimistic state */
    }
  }, []);

  // Generate (or re-generate) a single framework document for an organization.
  // Marks the key as 'generating' optimistically, calls the LLM-backed endpoint,
  // and merges the returned document into local state.
  const generateOrgDocument = useCallback(
    async (orgId: string, key: OrgDocumentKey): Promise<OrgDocumentGenerationResult> => {
      let result: OrgDocumentGenerationResult | null = null;
      setOrganizations((prev) =>
        prev.map((o) =>
          o.id === orgId
            ? {
                ...o,
                documentsStatus: { ...(o.documentsStatus || {}), [key]: 'generating' },
              }
            : o
        )
      );
      try {
        const res = await fetch('/api/org-document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgId, key }),
        });
        const data = (await res.json()) as
          | OrgDocumentGenerationResult
          | { error: string; code?: string };
        if (!res.ok) {
          const errMsg = (data as { error?: string }).error || 'Generation failed';
          setOrganizations((prev) =>
            prev.map((o) =>
              o.id === orgId
                ? {
                    ...o,
                    documentsStatus: { ...(o.documentsStatus || {}), [key]: 'error' },
                  }
                : o
            )
          );
          const e = new Error(errMsg) as Error & { code?: string };
          e.code = (data as { code?: string }).code;
          throw e;
        }
        result = data as OrgDocumentGenerationResult;
        setOrganizations((prev) =>
          prev.map((o) => {
            if (o.id !== orgId) return o;
            const nextDocs = { ...(o.documents || {}), [key]: { content: result!.content, generatedAt: result!.generatedAt } };
            const nextStatus = { ...(o.documentsStatus || {}), [key]: 'done' as const };
            return { ...o, documents: nextDocs, documentsStatus: nextStatus, updatedAt: new Date().toISOString() };
          })
        );
        return result;
      } catch (err) {
        setOrganizations((prev) =>
          prev.map((o) =>
            o.id === orgId
              ? {
                  ...o,
                  documentsStatus: { ...(o.documentsStatus || {}), [key]: 'error' },
                }
              : o
          )
        );
        throw err;
      }
    },
    []
  );

  // Forget a generated document so it can be regenerated. Local-only;
  // the server keeps the doc persisted until overwritten.
  const resetOrgDocument = useCallback((orgId: string, key: OrgDocumentKey) => {
    setOrganizations((prev) =>
      prev.map((o) => {
        if (o.id !== orgId) return o;
        const nextDocs = { ...(o.documents || {}) };
        delete nextDocs[key];
        const nextStatus = { ...(o.documentsStatus || {}) };
        delete nextStatus[key];
        return { ...o, documents: nextDocs, documentsStatus: nextStatus };
      })
    );
  }, []);

  const value = useMemo<OrganizationsContextValue>(
    () => ({
      organizations,
      activeOrganizationId,
      setActiveOrganizationId,
      createOrganization,
      updateOrganization,
      deleteOrganization,
      linkToProject,
      unlinkFromProject,
      refreshOrganizations,
      generateOrgDocument,
      resetOrgDocument,
      loadingOrganizations,
    }),
    [
      organizations,
      activeOrganizationId,
      loadingOrganizations,
      createOrganization,
      updateOrganization,
      deleteOrganization,
      linkToProject,
      unlinkFromProject,
      refreshOrganizations,
      generateOrgDocument,
      resetOrgDocument,
    ]
  );

  return <OrganizationsContext.Provider value={value}>{children}</OrganizationsContext.Provider>;
}
